import { createContext, PropsWithChildren, useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { NotFound } from "@/components/pages";
import { useOperator } from "@/hooks";
import { useGetVehicleQuery } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";

export interface IAddVehicleWizardContext {
  vehicleId: string;
  vehicle: Vehicle;
  refetchVehicle: () => Promise<void>;
  documentRetryCount: {
    accreditation: number;
    insurance: number;
  };
  setDocumentRetryCount: React.Dispatch<
    React.SetStateAction<{
      accreditation: number;
      insurance: number;
    }>
  >;
}

export const AddVehicleWizardContext = createContext<IAddVehicleWizardContext | null>(null);

export const AddVehicleWizardProvider = ({ children }: PropsWithChildren) => {
  const { id: operatorId } = useOperator();
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const [documentRetryCount, setDocumentRetryCount] = useState({
    accreditation: 0,
    insurance: 0,
  });

  const { data: vehicle, isLoading, isError, refetch } = useGetVehicleQuery(vehicleId ?? "", { skip: !vehicleId });

  const refetchVehicle = async () => {
    await refetch();
  };

  const isOperatorOwnedVehicle = useMemo(() => {
    return vehicle?.owner.uuid === operatorId;
  }, [operatorId, vehicle?.owner.uuid]);

  if (!vehicleId) {
    navigate("../", { replace: true });
    return null;
  }

  if (isError || (vehicle && !isOperatorOwnedVehicle)) return <NotFound noBackground noHeader />;
  if (isLoading || !vehicle) return <Loading />;

  return (
    <AddVehicleWizardContext.Provider value={{ vehicleId, vehicle, refetchVehicle, documentRetryCount, setDocumentRetryCount }}>
      {children}
    </AddVehicleWizardContext.Provider>
  );
};

export const useAddVehicleWizardContext = () => {
  const context = useContext(AddVehicleWizardContext);
  if (!context) {
    throw new Error("useAddVehicleWizardContext must be used within an AddVehicleWizardProvider");
  }
  return context;
};
